import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import Select, { MultiValue, SingleValue, components } from 'react-select';
import { AxiosResponse } from 'axios';

import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import useLoadingStore from 'zustands/useLoadingStore';
import { IOption } from 'types/common';
import { IAnnual, IPayForm, IPosition, IProbation, ISch, ISchItemDetail } from 'types/staff-registration';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import { daysOfWeekOptions, deducTypeOptions, payTypeOptions, positionOptions } from 'constants/dataArr';
import {
  addComma,
  formatNumberValueToKoreanTime,
  getChangeToKorean,
  onlyNumberInput,
  scheduleToMinValue,
} from 'constants/utils';
import ToggleButton from './toggleButton/toggleButton';
import PlainNavbar from 'components/plainNavbar/plainNavbar';
import { CheckCircle } from 'components/check-circle/check-circle';
import Button from 'common/button/button';
import DateSelector from 'common/date-selector/date-selector';
import Radio from 'common/radio/radio';
import CheckBox from 'common/checkbox/checkbox';
import Label from 'common/label/label';
import Toggle from 'common/toggle/toggle';
import TimeInput from 'common/time-input/time-input';
import { setSettings } from 'state/workSettingsSlice';
import { ReactComponent as PercentageIcon } from 'assets/icons/percentage.svg';
import { ReactComponent as EllipseIcon } from 'assets/icons/ellipse.svg';
import { ReactComponent as InfocircleIcon } from 'assets/icons/infocircle.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import { ReactComponent as CircleTextIcon } from 'assets/icons/circle-text.svg';
import './WorkSchedule.scss';
import { IEmpInfoData, ISchData, ISchList } from 'types/listOfEmployees';
import WorkingDetailsCard from '../viewInformation/workingDetailsCard/workingDetailsCard';
import useUserStore from 'zustands/useUserStore';
import useMinPayStore from 'zustands/useMinPayStore';

const WorkSchedule = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const empData: IEmpInfoData = location.state?.empData;

  const schData: ISchList = location.state?.schData;
  const formType: '인사정보' | '근무일정추가' | '근무일정수정' | '연차정보' | '근로계약' | '수습계약' =
    location.state?.formType;

  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();
  const { userType, managerInfo } = useUserStore();
  const { getMinPayByYear } = useMinPayStore();

  const { webApiNode, webApiPhp } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [extended, setExtended] = useState<boolean>(false);
  const dispatch = useDispatch();

  // const state = useSelector((state: IRootState) => state.workSettings);
  const [payForm, setPayForm] = useState<IPayForm>({
    appStartDate: new Date(), // 근로정보적용시작월
    startDate: new Date(), // 입사일
    endDate: moment().add(1, 'day').toDate(), // 퇴사일
    endDateUse: false, // 퇴사일 미정 사용 유무
    templates: [], // 근로템플릿
    slectedTemplate: { value: 'SELECT', label: '직접입력' }, // 선택한 템플릿
    selectedPayType: { value: '0', label: '시급' }, // 선택한 급여유형
    defaultPay: '', // 기본급
    hourPay_weekPay_use: false, // 주휴수당 자동 가산 체크 여부
    hourPay_weekPay_type: '1', // 1: 근로기준법 기준 10: 월근무시간
    hourPay_weekPay_time: '', // 월근무시간
    hourPay_restTime_use: false, // 휴게시간 자동 차감 체크 여부
    hourPay_restTime_type: '1', // 1: 근로기준법 기준 0: 월근무시간
    hourPay_restTime_time: '', // 월근무시간
    hourPay_allowance_use: false, // 3대 수당 자동 가산 체크 여부
    monthPay_meal: '', // 월급 - 식대
    monthPay_selfdrive: '', // 월급 - 자가운전
    monthPay_bonus: '', // 월급 - 상여
    monthPay_incentive: '', // 월급 - 성과급
    selectedDeducType: { value: '2', label: '4대보험' },
    deduc_penstion: false, // 국민연금
    deduc_health: false, // 건강보험
    deduc_employeement: false, // 고용보험
    deduc_accident: false, // 산재보험
  });
  // 수습계약
  const [probationForm, setProbationForm] = React.useState<IProbation>({
    use: false,
    endDate: moment().add(1, 'day').toDate(),
    rate: '',
  });
  // 직책/권한
  const [positionForm, setPositionForm] = React.useState<IPosition>({
    selectedPosition: { value: '1', label: '매니저' },
    position_allowMyPay: false,
    position_allowStorePay: false,
    position_allowOtherPayAndSch: false,
    position_allowModifyCommute: false,
    position_allowOtherPush: false,
  });
  // 연차
  const [annualForm, setAnnualForm] = React.useState<IAnnual>({
    annual_totalCount: '',
    annual_useCount: '',
    annual_remindCount: '',
    annual_startYear: new Date(),
  });

  // 일정정보
  const [schForm, setSchForm] = React.useState<ISch>({
    slectedTemplate: { value: 'SELECT', label: '직접입력' }, // 선택한 템플릿
    workType: 'FIXED', // 일정 타입 'FIXED': 고정, '': 자율출퇴근
    workStartDate: new Date(), // 일정 시작일
    workEndDate: moment().add(1, 'day').toDate(), // 일정 종료일
    workEndUse: false, // 일정 종료일 사용 유무
    workDaysOfWeek: [],
    workStartTime: '', // 근무 시작시간
    workEndTime: '', // 근무 종료시간
  });

  const [schList, setSchList] = React.useState<ISchList[]>([]);
  const [schDelList, setSchDelList] = React.useState<ISchData[]>([]);

  const [existingTemplate, setExistingTemplate] = React.useState<boolean>(false);
  const [currentExistingTemplate, setCurrentExistingTemplate] = React.useState<any>();

  const [localState, setLocalState] = useState<boolean[]>([true, true, true, true, true, true]);
  const [deleteSchList, setDeleteSchList] = React.useState<string[]>([]);
  const [currentMinPay, setCurrentMinPay] = React.useState<string>('');

  // 직원 정보 수정 권한 없는 경우 메인화면 이동 처리
  if (!empData) {
    navigate('/');
    setModalState({
      isModalShow: true,
      title: `선택된 직원 없음`,
    });
    return null;
  }

  // 서버 날짜 값이 null·빈 값이면 moment 가 Invalid Date 를 만들어 달력(DatePicker)이 깨지므로 기본값을 쓴다
  const toValidDate = (value: string | null | undefined, fallback: Date) =>
    value && moment(value).isValid() ? moment(value).toDate() : fallback;

  const setDefault = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiPhp('get', `/Employee/get?EMP_SEQ=${empData.EMP_SEQ}`);

      if (data.message === 'SUCCESS') {
        const EMPDATA = data.result;

        let payTypeName = '시급';
        if (EMPDATA.PAY_TYPE === '1') payTypeName = '일급';
        if (EMPDATA.PAY_TYPE === '2') payTypeName = '월급';
        let deducName = '4대보험';
        if (EMPDATA.insurance === '1') deducName = '3.3%';
        if (EMPDATA.insurance === '0') deducName = '없음';

        setPayForm({
          appStartDate: toValidDate(EMPDATA.PAY_START, new Date()), // 근로정보적용시작월
          startDate: toValidDate(EMPDATA.START, new Date()), // 입사일
          endDate: EMPDATA.END
            ? moment(EMPDATA.END).add(1, 'day').toDate()
            : moment(EMPDATA.START).add(1, 'day').toDate(), // 퇴사일
          endDateUse: EMPDATA.END ? true : false, // 퇴사일 미정 사용 유무
          templates: [], // 근로템플릿
          slectedTemplate: { value: 'SELECT', label: '직접입력' }, // 선택한 템플릿
          selectedPayType: { value: EMPDATA.PAY_TYPE, label: payTypeName }, // 선택한 급여유형
          defaultPay: addComma(EMPDATA.PAY), // 기본급
          hourPay_weekPay_use: EMPDATA.WEEKEND_ALLOWANCE === '1', // 주휴수당 자동 가산 체크 여부
          hourPay_weekPay_type: EMPDATA.WeekType, // 1: 근로기준법 기준 10: 월근무시간
          hourPay_weekPay_time: EMPDATA.WeekTime.toString(), // 월근무시간
          hourPay_restTime_use: EMPDATA.REST_ALLOWANCE === '1', // 휴게시간 자동 차감 체크 여부
          hourPay_restTime_type: EMPDATA.RestType, // 1: 근로기준법 기준 0: 월근무시간
          hourPay_restTime_time: EMPDATA.RestTime.toString(), // 월근무시간
          hourPay_allowance_use: EMPDATA.THREE_ALLOWANCE === '1', // 3대 수당 자동 가산 체크 여부
          monthPay_meal: addComma(EMPDATA.MEALS), // 월급 - 식대
          monthPay_selfdrive: addComma(EMPDATA.SELF_DRIVING), // 월급 - 자가운전
          monthPay_bonus: addComma(EMPDATA.BONUS), // 월급 - 상여
          monthPay_incentive: addComma(EMPDATA.INCENTIVE), // 월급 - 성과급
          selectedDeducType: { value: EMPDATA.insurance, label: deducName },
          deduc_penstion: EMPDATA.pension === '1', // 국민연금
          deduc_health: EMPDATA.health === '1', // 건강보험
          deduc_employeement: EMPDATA.employment === '1', // 고용보험
          deduc_accident: EMPDATA.accident === '1', // 산재보험
        });
        setProbationForm({
          use: EMPDATA.probation === '1',
          endDate: moment(toValidDate(EMPDATA.probationDATE, new Date())).add(1, 'day').toDate(),
          rate: EMPDATA.probationPercent,
        });
        setPositionForm({
          selectedPosition: { value: EMPDATA.IS_MANAGER, label: EMPDATA.IS_MANAGER === '1' ? '매니저' : '스태프' },
          position_allowMyPay: EMPDATA.PAY_SHOW === '1',
          position_allowStorePay: EMPDATA.STOREPAY_SHOW === '1',
          position_allowOtherPayAndSch: EMPDATA.OTHERPAY_SHOW === '1',
          position_allowModifyCommute: EMPDATA.CalendarEdit === '1',
          position_allowOtherPush: EMPDATA.PUSH === '1',
        });
        setAnnualForm({
          annual_totalCount: EMPDATA.Annual.toString(),
          annual_useCount: EMPDATA.USE_Annual.toString(),
          annual_remindCount: (EMPDATA.Annual - EMPDATA.USE_Annual).toString(),
          annual_startYear: toValidDate(EMPDATA.Annual_START, new Date()),
        });
        if (schData) {
          setSchForm({
            slectedTemplate: { value: 'SELECT', label: '직접입력' }, // 선택한 템플릿
            workType: EMPDATA.CALENDAR === '0' ? 'FIXED' : 'AUTONOMOUS', // 일정 타입 'FIXED': 고정, '': 자율출퇴근
            workStartDate: moment(schData.START).toDate(), // 일정 시작일
            workEndDate: schData.END ? moment(schData.END).toDate() : moment(schData.START).add(1, 'day').toDate(), // 일정 종료일
            workEndUse: schData.END !== null ? true : false, // 일정 종료일 사용 유무
            workDaysOfWeek: [],
            workStartTime: '', // 근무 시작시간
            workEndTime: '', // 근무 종료시간
          });
          setSchList([schData]);
        }
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /Employee/get');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };
  // 최저시급 계산 함수
  const calculateMinPay = (appStartDate: Date) => {
    const appYear = Number(moment(appStartDate).format('YYYY'));

    if (appYear >= 2023) {
      let minPay = getMinPayByYear(appYear.toString());

      // 해당 년도 데이터가 없으면 가장 최신 년도의 최저시급 사용
      if (!minPay) {
        const { minPays } = useMinPayStore.getState();
        const sortedMinpays = [...minPays].sort((a, b) => Number(b.YEAR) - Number(a.YEAR));
        if (sortedMinpays.length > 0) {
          minPay = sortedMinpays[0].MINPAY;
        }
      }

      setCurrentMinPay(minPay || '');
    } else {
      setCurrentMinPay('');
    }
  };

  // 날짜 입력 인풋 핸들
  const handleDateChange = (date: Date, type: string) => {
    // 급여 적용 월
    if (type === 'appStartDate') {
      setPayForm((prev) => ({ ...prev, appStartDate: date }));
      calculateMinPay(date);
    }
    // 입사일
    if (type === 'payForm_start') {
      if (moment(date).isAfter(payForm.endDate)) {
        setPayForm((prev) => ({ ...prev, startDate: date, endDate: moment(date).add(1, 'day').toDate() }));
      } else {
        setPayForm((prev) => ({ ...prev, startDate: date }));
      }
      if (moment(date).isAfter(probationForm.endDate)) {
        setProbationForm((prev) => ({ ...prev, endDate: moment(date).add(1, 'day').toDate() }));
      }
    }
    // 퇴사일
    if (type === 'payForm_end') {
      setPayForm((prev) => ({ ...prev, endDate: date }));
    }
    // 수습종료일
    if (type === 'probationForm_end') {
      setProbationForm((prev) => ({ ...prev, endDate: date }));
    }
    // 일정시작일
    if (type === 'schForm_workStart') {
      if (moment(date).isAfter(schForm.workEndDate)) {
        setSchForm((prev) => ({ ...prev, workStartDate: date, workEndDate: moment(date).add(1, 'day').toDate() }));
      } else {
        setSchForm((prev) => ({ ...prev, workStartDate: date }));
      }
      updateSchListWorkDate();
    }
    // 일정종료일
    if (type === 'schForm_workEnd') {
      setSchForm((prev) => ({ ...prev, workEndDate: date }));
      updateSchListWorkDate();
    }
    // 연차적용년도
    if (type === 'annual_year') {
      setAnnualForm((prev) => ({ ...prev, annual_startYear: date }));
    }
  };

  // 시급 - 수당 항목 선택 핸들러
  const handleChecks = (value: boolean, name: string) => {
    if (name.includes('sch_')) {
      const key = name.split('sch_')[1];
      setSchForm((prev) => ({
        ...prev,
        [key]: value,
      }));
      if (name === 'sch_workEndUse') {
        updateSchListWorkDate();
      }
    } else {
      setPayForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // 셀렉트 핸들
  const handleTypeChange = (value: SingleValue<IOption>, type: string) => {
    if (value) {
      if (type === 'pay') {
        const isNotMonthPay = value.label === '시급' || value.label === '일급';
        setPayForm((prev) => ({
          ...prev,
          selectedPayType: value,
          monthPay_meal: isNotMonthPay ? '' : prev.monthPay_meal,
          monthPay_selfdrive: isNotMonthPay ? '' : prev.monthPay_selfdrive,
          monthPay_bonus: isNotMonthPay ? '' : prev.monthPay_bonus,
          monthPay_incentive: isNotMonthPay ? '' : prev.monthPay_incentive,
        }));
      } else if (type === 'deduc') {
        setPayForm((prev) => ({
          ...prev,
          selectedDeducType: value,
          deduc_penstion: value.label === '4대보험' ? true : false,
          deduc_health: value.label === '4대보험' ? true : false,
          deduc_employeement: value.label === '4대보험' ? true : false,
          deduc_accident: value.label === '4대보험' ? true : false,
        }));
      } else if (type === 'position') {
        setPositionForm((prev) => ({
          ...prev,
          selectedPosition: value,
        }));
      }
    }
  };

  // 직책/권한 설정 항목 토글
  const togglePositionItem = (
    name:
      | 'position_allowMyPay'
      | 'position_allowStorePay'
      | 'position_allowOtherPayAndSch'
      | 'position_allowModifyCommute'
      | 'position_allowOtherPush',
  ) => {
    setPositionForm((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  // 연차 인풋 입력 핸들
  const handleAnnualValueInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAnnualForm((prev) => ({ ...prev, [name]: onlyNumberInput(value) }));
  };

  // 금액 입력 인풋 핸들
  const handleNumberValueInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPayForm((prev) => ({ ...prev, [name]: addComma(onlyNumberInput(value.split(',').join(''))) }));
  };

  // 시간 입력 인풋 핸들
  const handleTimeValueInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPayForm((prev) => ({ ...prev, [name]: Number(onlyNumberInput(value)).toString() }));
  };

  // 시급 - 수당 항목 계산 방법 체크 핸들러
  const handleChecksType = (value: string, name: string) => {
    setPayForm((prev) => ({
      ...prev,
      [name]: value,
      hourPay_weekPay_time: name === 'hourPay_weekPay_type' ? '0' : prev.hourPay_weekPay_time,
      hourPay_restTime_time: name === 'hourPay_restTime_type' ? '0' : prev.hourPay_restTime_time,
    }));
  };

  // 4대보험 항목 체크박스 핸들러
  const handleCheckBoxInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = event.target;
    setPayForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // 수습계약 사용 토글
  const toggleContract = () => {
    setProbationForm((prev) => ({ ...prev, use: !prev.use }));
  };

  // 수습 급여 비율 인풋 핸들
  const handlePercentValueInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numberValue = Number(onlyNumberInput(value));
    setProbationForm((prev) => ({
      ...prev,
      [name]: numberValue > 100 ? '100' : onlyNumberInput(value),
    }));
  };

  // 근무유형 선택
  const handleSelectWorkingMethod = (method: 'FIXED' | 'AUTONOMOUS') => {
    if (method === 'FIXED') {
      setSchForm((prev) => ({ ...prev, workType: method }));
    } else {
      setSchForm((prev) => ({ ...prev, workType: method }));
    }
  };

  // 근무요일 선택
  const handleWorkDayOfWeek = (newValue: MultiValue<IOption>) => {
    setSchForm((prev) => ({ ...prev, workDaysOfWeek: newValue }));
  };

  // 일정 근무시간 선택
  const handleWorkTime = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
    const { value } = e.target;
    setSchForm((prev) => ({ ...prev, [name]: value }));
  };

  // 수정 유형별 데이터 유효성 체크
  const validCheckForm = () => {
    if (!empData) {
      navigate('/list-of-employees');
      return setModalState({
        isModalShow: true,
        title: '정보 수정',
        desc: `선택된 직원이 없습니다.`,
      });
    }
    if (formType === '인사정보') {
      updateEmpWorkInfo();
    } else if (formType === '근로계약') {
      // 급여정보
      if (payForm.defaultPay === '') {
        return setModalState({
          isModalShow: true,
          title: '',
          desc: '급여정보 기본급을 입력해 주세요',
        });
      }
      if (payForm.selectedPayType?.label === '시급') {
        // 최저시급 체크 (2023년 이후만)
        if (currentMinPay) {
          const inputPay = Number((payForm.defaultPay || '').split(',').join(''));
          const appYear = moment(payForm.appStartDate).format('YYYY');

          if (inputPay < Number(currentMinPay)) {
            return setModalState({
              isModalShow: true,
              title: '급여정보',
              desc: `${appYear}년 최저시급(${addComma(
                currentMinPay,
              )}원)보다 낮은 금액입니다.\n최저시급 이상으로 입력해 주세요.`,
            });
          }
        }

        // 주휴수당 자동가산, 월 근무시간 체크 시 시간
        if (
          payForm.hourPay_weekPay_use &&
          payForm.hourPay_weekPay_type === '0' &&
          payForm.hourPay_weekPay_time === ''
        ) {
          return setModalState({
            isModalShow: true,
            title: '주휴수당 자동 가산',
            desc: '월 근무시간을 입력해 주세요',
          });
        }
        // 주휴수당 자동가산, 월 근무시간 체크 시 시간
        if (
          payForm.hourPay_restTime_use &&
          payForm.hourPay_restTime_type === '0' &&
          payForm.hourPay_restTime_time === ''
        ) {
          return setModalState({
            isModalShow: true,
            title: '휴게시간 자동 차감',
            desc: '일 휴게시간을 입력해 주세요',
          });
        }
        // 휴게시간 자동차감, 일 휴게시간 체크 시 시간
      }
      updateEmpWorkInfo();
    } else if (formType === '수습계약') {
      // 수습기간 사용 && 급여지급율 값 체크
      if (probationForm.use && probationForm.rate === '') {
        return setModalState({
          isModalShow: true,
          title: '수습계약',
          desc: '수습 급여지급율을 입력해 주세요',
        });
      }
      updateEmpWorkInfo();
    } else if (formType === '연차정보') {
      const totalAnnual = annualForm.annual_totalCount === '' ? 0 : Number(annualForm.annual_totalCount);
      const useAnnual = annualForm.annual_useCount === '' ? 0 : Number(annualForm.annual_useCount);
      const remindAnnual = annualForm.annual_remindCount === '' ? 0 : Number(annualForm.annual_remindCount);
      if (totalAnnual - useAnnual !== remindAnnual) {
        return setModalState({
          isModalShow: true,
          title: '연차정보',
          desc: '올바른 연차 수를 입력해 주세요\n[총 연차 수 - 사용 연차 = 남은 연차]',
        });
      }
      updateEmpWorkInfo();
    } else {
      // 생성된 추가할 일정 체크
      if (schList.length === 0) {
        return setModalState({
          isModalShow: true,
          title: '일정정보',
          desc: '일정을 선택하고 [+추가] 버튼을 눌러 추가할 일정을 생성해 주세요',
        });
      }

      console.log('@#!#!@');

      console.log('schDelList', schDelList);

      if (schDelList.length > 0) {
        updateEmpSchInfo(schDelList.map((i) => ({ ...i, USE_FLAG: '0' })));
      } else {
        updateEmpSchInfo();
      }
      // removeSch();
    }
  };

  // 직원정보 1차 저장 요청
  const updateEmpWorkInfo = async () => {
    try {
      const obj = {
        TEMPLATE_FLAG: false, // 탬플릿사용여부
        ET_SEQ: empData.EMP_SEQ, // 탬플릿seq
        START: moment(payForm.startDate).format('YYYY-MM-DD'), // 시작일
        END: payForm.endDateUse ? moment(payForm.endDate).format('YYYY-MM-DD') : null, // 종료일
        PAY_TYPE: payForm.selectedPayType?.value, // 급여유형
        PAY: payForm.defaultPay === '' ? '0' : payForm.defaultPay?.split(',').join('') ?? '0', // 급여
        PAY_START: moment(payForm.appStartDate).format('YYYY-MM'), // 적용월
        WEEKEND_ALLOWANCE: payForm.hourPay_weekPay_use ? '1' : '0', // 주휴수당여부
        WeekType: payForm.hourPay_weekPay_type, // 1:근로기준 0: 직접입력
        WeekTime: payForm.hourPay_weekPay_time === '' ? '0' : payForm.hourPay_weekPay_time, // 주휴수당 분
        REST_ALLOWANCE: payForm.hourPay_restTime_use ? '1' : '0', // 휴게시간여부
        RestType: payForm.hourPay_restTime_type, // 1:근로기준 0: 직접입력
        RestTime: payForm.hourPay_restTime_time === '' ? '0' : payForm.hourPay_restTime_time, // 휴게시간
        THREE_ALLOWANCE: payForm.hourPay_allowance_use ? '1' : '0', // 3대수당여부

        insurance: payForm.selectedDeducType?.value, // 2:4대보험 1:프리랜서 0:없음
        pension: payForm.deduc_penstion ? '1' : '0', // 국민연금
        health: payForm.deduc_health ? '1' : '0', // 건강보험
        employment: payForm.deduc_employeement ? '1' : '0', // 고용보험
        accident: payForm.deduc_accident ? '1' : '0', // 산재보험
        MEALS: !payForm.monthPay_meal ? '0' : payForm.monthPay_meal.split(',').join(''), // 식대
        SELF_DRIVING: !payForm.monthPay_selfdrive ? '0' : payForm.monthPay_selfdrive.split(',').join(''), // 자가운전
        BONUS: !payForm.monthPay_bonus ? '0' : payForm.monthPay_bonus.split(',').join(''), // 상여
        INCENTIVE: !payForm.monthPay_incentive ? '0' : payForm.monthPay_incentive.split(',').join(''), // 인센티브
        probation: probationForm.use ? '1' : '0', // 수습여부
        probationDATE: moment(probationForm.endDate).format('YYYY-MM-DD'), // 수습종료일
        probationPercent: probationForm.rate, // 수습퍼센트
        EMP_SEQ_LIST: [{ EMP_SEQ: empData.EMP_SEQ }], // 직원 인덱스배열
        IS_MANAGER: positionForm.selectedPosition.value, // 매니저
        PAY_SHOW: positionForm.position_allowMyPay ? '1' : '0', // 급여보기
        STOREPAY_SHOW: positionForm.position_allowStorePay ? '1' : '0', // 매장급여보기
        OTHERPAY_SHOW: positionForm.position_allowOtherPayAndSch ? '1' : '0', // 타직원급여보기
        CalendarEdit: positionForm.position_allowModifyCommute ? '1' : '0', // 타직원일정수정
        PUSH: positionForm.position_allowOtherPush ? '1' : '0', // 푸시받기
        Annual: annualForm.annual_totalCount === '' ? '0' : annualForm.annual_totalCount, // 총연차
        USE_Annual: annualForm.annual_useCount === '' ? '0' : annualForm.annual_useCount, // 사용연차
        RemainderAnnual: annualForm.annual_remindCount === '' ? '0' : annualForm.annual_remindCount, // 남은연차
        Annual_START: moment(annualForm.annual_startYear).year(), // 연차적용년
      };

      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/updateEmpInfo`, obj);

      if (data.resultCode === 0) {
        navigate(-1);
        setModalState({
          isModalShow: true,
          title: formType,
          desc: '수정 완료되었습니다',
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/updateEmpInfo');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 2단계 일정정보 데이터 체크
  const validCheckSchForm = () => {
    // 근무요일 체크
    if (schForm.workType === 'FIXED') {
      if (schForm.workDaysOfWeek.length === 0) {
        return setModalState({
          isModalShow: true,
          title: '일정정보',
          desc: '근무 요일을 선택해 주세요',
        });
      }
      // 근무 시작시간 체크
      if (schForm.workStartTime === '') {
        return setModalState({
          isModalShow: true,
          title: '일정정보',
          desc: '근무 시작시간을 선택해 주세요',
        });
      }
      // 근무 종료시간 체크
      if (schForm.workEndTime === '') {
        return setModalState({
          isModalShow: true,
          title: '일정정보',
          desc: '근무 종료시간을 선택해 주세요',
        });
      }
    }
    return true;
  };

  // 선택한 일정 추가
  const addSchListItem = () => {
    if (!validCheckSchForm()) return false;
    const daysItem: ISchData[] = schForm.workDaysOfWeek.map((i) => {
      return {
        EMP_SEQ: empData.EMP_SEQ,
        EMP_SCH_SEQ: '-1',
        DAY: i.value,
        ATTENDANCE_TIME: schForm.workStartTime,
        WORK_OFF_TIME: schForm.workEndTime,
        USE_FLAG: '1',
        START: moment(schForm.workStartDate).format('YYYY-MM-DD'),
        END: schForm.workEndUse ? moment(schForm.workEndDate).format('YYYY-MM-DD') : null,
      };
    });

    const findSchIdx = schList.findIndex(
      (i) =>
        i.START === moment(schForm.workStartDate).format('YYYY-MM-DD') &&
        i.END === (schForm.workEndUse ? moment(schForm.workEndDate).format('YYYY-MM-DD') : null),
    );

    if (findSchIdx !== -1) {
      const formatSchList: ISchList[] = schList.map((i, _i) => {
        if (_i === findSchIdx) {
          let hasTime = false;
          const list = i.data.map((j) => {
            if (j[0].ATTENDANCE_TIME === schForm.workStartTime && j[0].WORK_OFF_TIME === schForm.workEndTime) {
              hasTime = true;
              return [...j, ...daysItem];
            } else {
              return j;
            }
          });
          if (!hasTime) list.push([...daysItem]);
          return {
            ...i,
            data: list,
          };
        }
        return i;
      });
      setSchList(formatSchList);
    } else {
      const formatSchList: ISchList = {
        START: moment(schForm.workStartDate).format('YYYY-MM-DD'),
        END: schForm.workEndUse ? moment(schForm.workEndDate).format('YYYY-MM-DD') : null,
        data: [daysItem],
      };
      setSchList([...schList, formatSchList]);
    }
    // 선택했던 일정옵션 초기화
    setSchForm((prev) => ({
      ...prev,
      workDaysOfWeek: [],
      workStartTime: '', // 근무 시작시간
      workEndTime: '', // 근무 종료시간
    }));
  };

  console.log('schDelList', schDelList);

  // 추가목록에 있는 일정 삭제
  const removeSchListItem = (start: string, end: string | null, startTime: string, endTime: string) => {
    let listWithoutTime: ISchList[] = [];
    for (let i = 0; i < schList.length; i++) {
      if (schList[i].START === start && schList[i].END === end) {
        const delList = [...schDelList];
        const timeArr = schList[i].data.filter((j) => {
          if (j[0].ATTENDANCE_TIME === startTime && j[0].WORK_OFF_TIME === endTime) {
            j.forEach((k) => {
              if (delList.findIndex((l) => l.EMP_SCH_SEQ === k.EMP_SCH_SEQ) === -1 && k.EMP_SCH_SEQ !== '-1') {
                delList.push(k);
              }
            });
            return false;
          }
          return true;
        });
        setSchDelList(delList);
        if (timeArr.length > 0) {
          listWithoutTime = [...listWithoutTime, { ...schList[i], data: timeArr }];
        }
      } else {
        listWithoutTime = [...listWithoutTime, schList[i]];
      }
    }
    listWithoutTime = listWithoutTime.filter((i) => i.data.length > 0 && i.data[0].length > 0);
    setSchList(listWithoutTime);
  };

  // 일정기간 변경 시 추가된 일정아이템의 일정기간 변경
  const updateSchListWorkDate = () => {
    if (schList.length > 0) {
      const newSchList = schList.map((i) => {
        return {
          ...i,
          START: moment(schForm.workStartDate).format('YYYY-MM-DD'),
          END: schForm.workEndUse ? moment(schForm.workEndDate).format('YYYY-MM-DD') : null,
        };
      });
      setSchList(newSchList);
    }
  };

  // 일정시작,종료,미정 변경 시 추가된 일정아이템 기간 수정
  useEffect(() => {
    updateSchListWorkDate();
  }, [schForm.workStartDate, schForm.workEndDate, schForm.workEndUse]);

  // 기존 일정 지우고 새로 생성하는 경우 기존 일정 삭제
  const removeSch = async () => {
    if (schDelList.length > 0) {
      updateEmpSchInfo(schDelList.map((i) => ({ ...i, USE_FLAG: '0' })));

      try {
        setLoadingState({ isFetchLoading: true });
        const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/updateEmpSchedule`, {
          START: schData.START,
          END_FLAG: schData.END ? '1' : '0',
          empSchedules: schDelList.map((i) => ({ ...i, USE_FLAG: '0' })),
        });
        if (data.resultCode === 0) {
          if (deleteSchList.length > 0) {
            const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/schedule`, {
              DEL: deleteSchList,
            });
          }
          updateEmpSchInfo(schDelList.map((i) => ({ ...i, USE_FLAG: '0' })));
        } else {
          setLoadingState({ isFetchLoading: false });
          const title = (data.title ?? '일정 저장 실패').replace(/\\n/g, '\n');
          const desc = (data.description ?? data.resultMsg ?? '').replace(/\\n/g, '\n');
          setModalState({ isModalShow: true, title, desc });
        }
      } catch (err: any) {
        setLoadingState({ isFetchLoading: false });
        const resData = err?.response?.data;
        if (resData?.title != null || resData?.description != null) {
          const title = (resData.title ?? '일정 저장 실패').replace(/\\n/g, '\n');
          const desc = (resData.description ?? resData.resultMsg ?? '').replace(/\\n/g, '\n');
          setModalState({ isModalShow: true, title, desc });
        } else {
          checkErrorLog(err, ' /auth/updateEmpSchedule');
        }
      } finally {
      }
    } else {
      updateEmpSchInfo(schDelList.map((i) => ({ ...i, USE_FLAG: '0' })));
    }
  };

  // 일정정보 저장
  const updateEmpSchInfo = async (schDelList?: ISchData[]) => {
    try {
      const empScheduleArr: ISchData[] = [];
      schList.map((sch) => {
        sch.data.map((j) => {
          j.map((k) => {
            empScheduleArr.push({
              EMP_SEQ: empData.EMP_SEQ,
              EMP_SCH_SEQ: k.EMP_SCH_SEQ,
              DAY: k.DAY,
              ATTENDANCE_TIME: k.ATTENDANCE_TIME,
              WORK_OFF_TIME: k.WORK_OFF_TIME,
              USE_FLAG: '1',
              START: moment(schForm.workStartDate).format('YYYY-MM-DD'),
              END: schForm.workEndUse ? moment(schForm.workEndDate).format('YYYY-MM-DD') : null,
            });
          });
        });
      });
      const obj = {
        START: moment(schForm.workStartDate).format('YYYY-MM-DD'),
        END_FLAG: schForm.workEndUse ? '0' : '1',
        empSchedules: schDelList && schDelList?.length > 0 ? empScheduleArr.concat(schDelList) : empScheduleArr,
      };

      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/updateEmpSchedule`, obj);
      if (data.resultCode === 0) {
        if (deleteSchList.length > 0) {
          const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/schedule`, {
            DEL: deleteSchList,
          });
        }
        setModalState({
          isModalShow: true,
          title: `일정 ${formType === '근무일정추가' ? '추가' : '수정'}`,
          desc: '완료되었습니다',
        });
        navigate(-1);
      } else {
        setLoadingState({ isFetchLoading: false });
        setModalState({
          isModalShow: true,
          title: (data.title ?? '일정 저장 실패').replace(/\\n/g, '\n'),
          desc: (data.description ?? data.resultMsg ?? '').replace(/\\n/g, '\n'),
        });
        return;
      }
    } catch (err: any) {
      setLoadingState({ isFetchLoading: false });
      const resData = err?.response?.data;
      if (resData?.title != null || resData?.description != null) {
        setModalState({
          isModalShow: true,
          title: (resData.title ?? '일정 저장 실패').replace(/\\n/g, '\n'),
          desc: (resData.description ?? resData.resultMsg ?? '').replace(/\\n/g, '\n'),
        });
      } else {
        checkErrorLog(err, ' /auth/updateEmpSchedule');
      }
    } finally {
    }
  };

  const settings = [
    // '본인급여 확인 가능',
    '사업장 급여 확인 가능',
    '직원 급여 / 일정 확인 및 수정 가능',
    '출퇴근기록 수정 가능',
    '타직원 출퇴근 알람 받기',
  ];

  useEffect(() => {
    if (empData) {
      setDefault();
    }
  }, []);

  // 급여 적용월 변경 시 최저시급 계산
  useEffect(() => {
    if (payForm.appStartDate) {
      calculateMinPay(payForm.appStartDate);
    }
  }, [payForm.appStartDate]);

  const colourStyles = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '56px',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  // 추가된 일정의 요일 제거
  const unSelectedDayArr = daysOfWeekOptions.filter((i) => {
    const selected: string[] = [];
    schList?.map((j, _j) => {
      j.data?.map((k) => {
        k?.map((l) => {
          if (!selected.includes(l.DAY)) {
            selected.push(l.DAY);
          }
        });
      });
    });
    return !selected.includes(i.value);
  });

  return (
    <>
      <PlainNavbar
        backgroundColor="#f3f4f6"
        customButton={
          <div className="plainNavbarFooter__action">
            <div className="plainNavbarFooter__cancel">
              <Button label="취소" type="type1" height={42} action={() => navigate(-1)} />
            </div>
            <div>
              <Button
                label={formType === '근무일정추가' ? '추가하기' : '수정하기'}
                type="type2"
                height={42}
                action={validCheckForm}
              />
            </div>
          </div>
        }>
        <div className="workScheduleScreen">
          <div className="container">
            <div className="breadCrumb">
              <div className="mainScreen">직원목록</div> {'>'}
              <div className="secondScreen">인사정보</div>
            </div>

            <div className="nameContainer">
              <div className="image">{empData.EMP_NAME.substring(1).substr(0, 2)}</div>
              <div className="nameText">{empData.EMP_NAME}</div>
            </div>

            {formType === '인사정보' && (
              <>
                <div className="basicInfoCard">
                  <div className="leftContainer">
                    <div className="title mb-4">기본정보</div>
                    <div className="dateInputWrapper">
                      <DateSelector
                        onChange={(date) => handleDateChange(date, 'payForm_start')}
                        value={payForm.startDate}
                        label="입사일*"
                        height={56}
                      />
                      <div className="spacer"></div>
                      <DateSelector
                        onChange={(date) => handleDateChange(date, 'payForm_end')}
                        value={payForm.endDate}
                        label="퇴사일*"
                        height={56}
                        minDate={payForm.startDate}
                        hasDisabled={true}
                        disabled={!payForm.endDateUse}
                        toggleDisable={(value: boolean) => handleChecks(value, 'endDateUse')}
                      />
                    </div>
                  </div>
                  <div className="rightContainer">
                    <div className="title">퇴사일이란?</div>
                    <div className="description">정해진 퇴사일이 있다면 체크박스를 해제하고 퇴사일을 선택해 주세요</div>
                  </div>
                </div>

                <div className="staff-registration__card">
                  <div className="staff-registration__card__left">
                    <div className="staff-registration__permission">
                      <div className="staff-registration__permission__head">직책 / 권한 설정</div>
                      <div className="staff-registration__permission__body">
                        <div className="staff-registration__permission__body__select">
                          <div>직책</div>
                          <Select
                            className="staff-registration__payment__body__input4"
                            components={{
                              IndicatorSeparator: () => null,
                              DropdownIndicator: () => (
                                <div style={{ paddingRight: '15px' }}>
                                  <ArrowDownIcon />
                                </div>
                              ),
                            }}
                            placeholder="매니저"
                            styles={colourStyles}
                            value={positionForm.selectedPosition}
                            options={positionOptions}
                            onChange={(newValue) => handleTypeChange(newValue, 'position')}
                          />
                        </div>
                        <div className="staff-registration__permission__body__togglers">
                          <div className="staff-registration__permission__togglers__t1">
                            <div className="staff-registration__permission__togglers__t1__text">본인급여 확인 가능</div>
                            <div className="staff-registration__permission__togglers__t1__toggler">
                              <ToggleButton
                                toggled={positionForm.position_allowMyPay}
                                onClick={() => togglePositionItem('position_allowMyPay')}
                              />
                            </div>
                          </div>
                          {positionForm.selectedPosition.label === '매니저' && (
                            <>
                              <div className="staff-registration__permission__togglers__t1">
                                <div className="staff-registration__permission__togglers__t1__text">
                                  사업장 급여 확인 가능
                                </div>
                                <div className="staff-registration__permission__togglers__t1__toggler">
                                  <ToggleButton
                                    toggled={positionForm.position_allowStorePay}
                                    onClick={() => togglePositionItem('position_allowStorePay')}
                                  />
                                </div>
                              </div>
                              <div className="staff-registration__permission__togglers__t1">
                                <div className="staff-registration__permission__togglers__t1__text">
                                  직원 급여 / 일정 확인 및 수정 가능
                                </div>
                                <div className="staff-registration__permission__togglers__t1__toggler">
                                  <ToggleButton
                                    toggled={positionForm.position_allowOtherPayAndSch}
                                    onClick={() => togglePositionItem('position_allowOtherPayAndSch')}
                                  />
                                </div>
                              </div>
                              <div className="staff-registration__permission__togglers__t1">
                                <div className="staff-registration__permission__togglers__t1__text">
                                  출퇴근기록 수정 가능
                                </div>
                                <div className="staff-registration__permission__togglers__t1__toggler">
                                  <ToggleButton
                                    toggled={positionForm.position_allowModifyCommute}
                                    onClick={() => togglePositionItem('position_allowModifyCommute')}
                                  />
                                </div>
                              </div>
                              <div className="staff-registration__permission__togglers__t1">
                                <div className="staff-registration__permission__togglers__t1__text">
                                  타직원 출퇴근 알람 받기
                                </div>
                                <div className="staff-registration__permission__togglers__t1__toggler">
                                  <ToggleButton
                                    toggled={positionForm.position_allowOtherPush}
                                    onClick={() => togglePositionItem('position_allowOtherPush')}
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="staff-registration__card__right">
                    <div>
                      <div className="staff-registration__right-heads">직책</div>
                      <div className="staff-registration__right-body">
                        매니저는 사업주의 권한을 부분적으로 부여받아 사업주의 업무를 대행할 수 있어요
                      </div>
                    </div>
                    <div className="staff-registration__right-top">
                      <div className="staff-registration__right-heads">본인급여 확인</div>
                      <div className="staff-registration__right-body">
                        선택 시, 직원이 급여정보 화면에서 누적급여 등을 확인할 수 있어요
                      </div>
                    </div>
                    {positionForm.selectedPosition.label === '매니저' && (
                      <>
                        <div className="staff-registration__right-top">
                          <div className="staff-registration__right-heads">사업장 급여 확인</div>
                          <div className="staff-registration__right-body">
                            선택 시, 직원이 급여정보 화면에서 누적급여 등을 확인할 수 있어요
                          </div>
                        </div>
                        <div className="staff-registration__right-top">
                          <div className="staff-registration__right-heads">직원 급여/일정 확인 및 수정</div>
                          <div className="staff-registration__right-body">
                            직원의 정보수정(급여, 수당, 공제유형 등) 및 근무일정을 수정할 수 있어요
                          </div>
                        </div>
                        <div className="staff-registration__right-top">
                          <div className="staff-registration__right-heads">출퇴근기록 수정</div>
                          <div className="staff-registration__right-body">출퇴근 기록을 수정할 수 있어요</div>
                        </div>
                        <div className="staff-registration__right-top">
                          <div className="staff-registration__right-heads">타직원 출근 알림</div>
                          <div className="staff-registration__right-body">
                            직원의 출퇴근, 체크리스트 알람을 받아보실 수 있어요
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
            {formType.includes('근무일정') && (
              <div>
                <div className="staff-registration__card">
                  <div className="staff-registration__card__left">
                    <div className="staff-registration__schedule">
                      <div className="staff-registration__schedule__head">일정정보</div>
                      <div className="staff-registration__schedule__body">
                        <div className="staff-registration__schedule__body__select-template">
                          {schForm.slectedTemplate.label === '직접입력' && (
                            <div className="staff-registration__schedule__extend">
                              {/* Fixed */}
                              {schForm.workType === 'FIXED' && (
                                <div className="fixed-form">
                                  <div className="staff-registration__schedule__extend__way-of-work">
                                    <div className="staff-registration__schedule__extend__way-of-work__left">
                                      <EllipseIcon />
                                      <span className="staff-registration__schedule__extend__way-of-work__span">
                                        반복기간(시작-종료)
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', width: '75%', justifyContent: 'space-between' }}>
                                      <div style={{ flex: 0.4 }}>
                                        <DateSelector
                                          onChange={(date) => handleDateChange(date, 'schForm_workStart')}
                                          value={schForm.workStartDate}
                                          height={56}
                                        />
                                      </div>
                                      <div style={{ flex: 0.58 }}>
                                        <DateSelector
                                          onChange={(date) => handleDateChange(date, 'schForm_workEnd')}
                                          value={schForm.workEndDate}
                                          height={56}
                                          minDate={schForm.workStartDate}
                                          hasDisabled={true}
                                          disabled={!schForm.workEndUse}
                                          toggleDisable={(value: boolean) => handleChecks(value, 'sch_workEndUse')}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <div className="staff-registration__schedule__extend__way-of-work">
                                    <div className="staff-registration__schedule__extend__way-of-work__left">
                                      <EllipseIcon />
                                      <span className="staff-registration__schedule__extend__way-of-work__span">
                                        근무 요일
                                      </span>
                                    </div>
                                    <div className="staff-registration__schedule__extend__way-of-work__right">
                                      <Select
                                        className="staff-registration__schedule__body__input4"
                                        name="set-working-days"
                                        components={{
                                          IndicatorSeparator: () => null,
                                          DropdownIndicator: () => (
                                            <div style={{ paddingRight: '15px' }}>
                                              <ArrowDownIcon />
                                            </div>
                                          ),
                                          SingleValue: () => (
                                            <div style={{ paddingRight: '15px' }}>
                                              <ArrowDownIcon />
                                            </div>
                                          ),
                                        }}
                                        placeholder="근무요일 선택"
                                        styles={colourStyles}
                                        options={unSelectedDayArr}
                                        value={schForm.workDaysOfWeek}
                                        onChange={handleWorkDayOfWeek}
                                        isMulti
                                        hideSelectedOptions={false}
                                        isClearable={false}
                                      />
                                    </div>
                                  </div>

                                  <div className="staff-registration__schedule__extend__way-of-work">
                                    <div className="staff-registration__schedule__extend__way-of-work__left">
                                      <EllipseIcon />
                                      <span className="staff-registration__schedule__extend__way-of-work__span">
                                        근무 시간(시작-종료)
                                      </span>
                                    </div>
                                    <div className="staff-registration__schedule__extend__way-of-work__right">
                                      <div className="staff-registration__schedule__extend__way-of-work__time">
                                        {/* <TimePicker setData={setWorkingTime} /> */}
                                        <div
                                          style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                                          <div style={{ flex: 0.49 }}>
                                            <TimeInput
                                              value={schForm.workStartTime}
                                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                handleWorkTime(event, 'workStartTime')
                                              }
                                              iconSide="RIGHT"
                                            />
                                          </div>
                                          <div style={{ flex: 0.49 }}>
                                            <TimeInput
                                              value={schForm.workEndTime}
                                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                handleWorkTime(event, 'workEndTime')
                                              }
                                              iconSide="RIGHT"
                                            />
                                          </div>
                                        </div>
                                        {/* <div className="staff-registration__schedule__extend__way-of-work__rest-include">
                                      <Select
                                        className="staff-registration__schedule__body__input4"
                                        components={{
                                          IndicatorSeparator: () => null,
                                          DropdownIndicator: () => (
                                            <div
                                              style={{
                                                paddingRight: '15px',
                                              }}>
                                              <ArrowDownIcon />
                                            </div>
                                          ),
                                        }}
                                        placeholder="템플릿 선택"
                                        styles={colourStyles}
                                        options={breakTimeArray}
                                        // onChange={selectBreakTime}
                                      />
                                    </div> */}
                                      </div>
                                    </div>
                                  </div>
                                  {unSelectedDayArr.length > 0 && (
                                    <div className="staff-registration__list">
                                      <div className="staff-registration__list__plus">
                                        <div
                                          className="staff-registration__list__plus__button"
                                          onClick={addSchListItem}>
                                          +추가
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* {schForm.workDaysOfWeek.length > 0 &&
                                  schForm.workStartTime !== '' &&
                                  schForm.workEndTime !== '' && (
                                    <>
                                      <div className="staff-registration__schedule__extend__way-of-work">
                                        <div className="staff-registration__schedule__extend__way-of-work__left">
                                          <EllipseIcon />
                                          <span className="staff-registration__schedule__extend__way-of-work__span">
                                            총 근무 시간
                                          </span>
                                        </div>
                                      </div>
                                      <WorkingHoursLabel
                                        leftText={`주 ${formatNumberValueToKoreanTime(
                                          workTimeOfDay * schForm.workDaysOfWeek.length,
                                        )} 근무`}
                                        rightText="적합한 설정입니다"
                                        icon="tick"
                                      />
                                    </>
                                  )} */}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="staff-registration__card__right">
                    <div className="staff-registration__schedule-right">
                      {/* <div>
                      <div className="staff-registration__schedule-right__head">일정템플릿이란?</div>
                      <div className="staff-registration__right-body">
                        직원 일정 정보를 템플릿으로 저장해두면 여러 명의 직원에게 일정 정보를 한꺼번에 적용할 수 있어요
                      </div>
                    </div> */}
                      {schForm.workType === 'FIXED' && (
                        <div>
                          <div className="staff-registration__schedule-right__head" style={{ marginTop: 0 }}>
                            반복기간
                          </div>
                          <div className="staff-registration__right-body">
                            반복기간의 종료일을 미정으로 선택하면 새로운 일정을 등록할 때까지 해당 일정으로 근무합니다
                            <br />
                            <br />
                            반복기간을 변경하면 추가할 일정도 모두 동일하게 변경됩니다. 다른 반복기간이 필요한경우
                            하나씩 일정을 등록/수정해 주세요.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {schList.length > 0 && (
                  <div className="staff-registration__card">
                    <div className="staff-registration__card__left">
                      <div className="staff-registration__payment">
                        <div className="staff-registration__payment__head">추가할 일정</div>
                        <div className="staff-registration__payment__body">
                          <div className="staff-registration__list">
                            {schList.map((i, _i) => {
                              return (
                                <div
                                  key={`schListItem_${i.START}_${i.END} ${i.data[0][0].DAY}`}
                                  className="scheduleInfo">
                                  <div className="section">
                                    <div className="label">일정기간</div>
                                    <div className="value">
                                      {moment(i.START).format('YYYY.MM.DD')}
                                      {i.END ? ' ~ ' + moment(i.END).format('YYYY.MM.DD') : ' ~ 계속'}
                                    </div>
                                  </div>
                                  {i.data.length > 0 && (
                                    <div>
                                      {i.data.map((j) => {
                                        // 요일 배열
                                        const dayArr: string[] = [];
                                        j.map((k) => {
                                          dayArr.push(getChangeToKorean(k.DAY));
                                        });
                                        return (
                                          <WorkingDetailsCard
                                            key={`${i.START}_${i.END} ${j[0].ATTENDANCE_TIME} ~ ${
                                              j[0].WORK_OFF_TIME
                                            } ${dayArr.join(', ')}`}
                                            days={dayArr.join(', ')}
                                            time={`${j[0].ATTENDANCE_TIME} ~ ${j[0].WORK_OFF_TIME}`}
                                            hours={formatNumberValueToKoreanTime(
                                              scheduleToMinValue(j[0].ATTENDANCE_TIME, j[0].WORK_OFF_TIME),
                                            ).toString()}
                                            removeBtnText="지우기"
                                            onRemove={() => {
                                              removeSchListItem(
                                                i.START,
                                                i.END,
                                                j[0].ATTENDANCE_TIME,
                                                j[0].WORK_OFF_TIME,
                                              );

                                              if (j[0].EMP_SCH_SEQ.toString() !== '-1') {
                                                let tmp = [...deleteSchList, ...j.map((a) => a.EMP_SCH_SEQ)];
                                                setDeleteSchList(tmp);
                                              }
                                            }}
                                          />
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="staff-registration__card__right">
                      <div>
                        <div className="staff-registration__right-heads">추가할 일정</div>
                        <div className="staff-registration__right-body">
                          반복기간 동안 선택한 요일과 근무시간에 따라 출퇴근을 진행합니다
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {formType === '연차정보' && (
              <div className="staff-registration__card">
                <div className="staff-registration__card__left">
                  <div className="staff-registration__payment">
                    <div className="staff-registration__payment__head">연차 정보</div>
                    <div className="staff-registration__payment__body">
                      <div className="staff-registration__payment__body__application-start">
                        <DateSelector
                          onChange={(date) => handleDateChange(date, 'annual_year')}
                          value={annualForm.annual_startYear}
                          label="적용년도"
                          height={56}
                          isYearPicker={true}
                        />
                      </div>

                      <div
                        className="staff-registration__payment__body__select-template"
                        style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div className="staff-registration__payment__body__add-new-template">
                          <div className="staff-registration__payment__body__label">총 연차</div>
                          <div className="staff-registration__contract__body__pay-percentage-input-set">
                            <div className="staff-registration__contract__body__percentage">
                              <div className="staff-registration__payment__body__percentage__icon">일</div>
                              <input
                                className="staff-registration__contract__body__pay-percentage-input"
                                placeholder="총 연차 수 입력"
                                type="text"
                                name="annual_totalCount"
                                value={annualForm.annual_totalCount}
                                onChange={handleAnnualValueInput}
                                maxLength={2}
                                style={{ borderRadius: 8 }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="staff-registration__payment__body__add-new-template">
                          <div className="staff-registration__payment__body__label">사용 연차</div>
                          <div className="staff-registration__contract__body__pay-percentage-input-set">
                            <div className="staff-registration__contract__body__percentage">
                              <div className="staff-registration__payment__body__percentage__icon">일</div>
                              <input
                                className="staff-registration__contract__body__pay-percentage-input"
                                placeholder="사용 연차 수 입력"
                                type="text"
                                name="annual_useCount"
                                value={annualForm.annual_useCount}
                                onChange={handleAnnualValueInput}
                                maxLength={2}
                                style={{ borderRadius: 8 }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="staff-registration__payment__body__add-new-template">
                          <div className="staff-registration__payment__body__label">남은 연차</div>
                          <div className="staff-registration__contract__body__pay-percentage-input-set">
                            <div className="staff-registration__contract__body__percentage">
                              <div className="staff-registration__payment__body__percentage__icon">일</div>
                              <input
                                className="staff-registration__contract__body__pay-percentage-input"
                                placeholder="남은 연차 수 입력"
                                type="text"
                                name="annual_remindCount"
                                value={annualForm.annual_remindCount}
                                onChange={handleAnnualValueInput}
                                maxLength={2}
                                style={{ borderRadius: 8 }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="staff-registration__card__right">
                  <div>
                    <div className="staff-registration__right-heads">적용년도</div>
                    <div className="staff-registration__right-body">해당년도부터 연차가 적용됩니다.</div>
                  </div>
                </div>
              </div>
            )}
            {formType === '근로계약' && (
              <div className="staff-registration__card">
                <div className="staff-registration__card__left">
                  <div className="staff-registration__payment">
                    <div className="staff-registration__payment__head">급여 정보(필수)</div>
                    <div className="staff-registration__payment__body">
                      <div className="staff-registration__payment__body__select-template">
                        {/* <div className="staff-registration__payment__body__select-template-head">
                        근로템플릿 선택*
                      </div>
                      <div className="staff-registration__payment__body__select-template-select">
                        <Select
                          className="staff-registration__payment__body__input"
                          components={{
                            IndicatorSeparator: () => null,
                            DropdownIndicator: () => (
                              <div style={{ paddingRight: '15px' }}>
                                <ArrowDownIcon />
                              </div>
                            ),
                            Option: IconOption,
                          }}
                          placeholder="템플릿 선택"
                          styles={colourStyles}
                          value={payForm.slectedTemplate}
                          options={templateOptions}
                          onChange={handleTemplateSelect}
                          isDisabled={true}
                        />

                        <div className="staff-registration__payment__body__text-template">근로템플릿 설정</div>
                      </div> */}
                        <div className="staff-registration__payment__body__add-new-template">
                          <DateSelector
                            onChange={(date) => handleDateChange(date, 'appStartDate')}
                            value={payForm.appStartDate}
                            label="급여 정보 적용 시작월*"
                            height={56}
                            isMonthPicker={true}
                          />
                        </div>
                        {payForm.slectedTemplate?.label === '직접입력' && (
                          <div className="staff-registration__payment__body__add-new-template">
                            <div className="staff-registration__payment__body__label">급여정보*</div>
                            <div className="staff-registration__payment__body__add-new-template-input">
                              <div className="staff-registration__payment__body__add-new-template-input-select">
                                <Select
                                  className="staff-registration__payment__body__input2"
                                  components={{
                                    IndicatorSeparator: () => null,
                                    DropdownIndicator: () => (
                                      <div style={{ paddingRight: '15px' }}>
                                        <ArrowDownIcon />
                                      </div>
                                    ),
                                  }}
                                  placeholder="시급"
                                  styles={colourStyles}
                                  value={payForm.selectedPayType}
                                  options={payTypeOptions}
                                  onChange={(newValue) => handleTypeChange(newValue, 'pay')}
                                />
                              </div>
                              <div className="staff-registration__payment__body__percentage">
                                <div>
                                  <CircleTextIcon className="staff-registration__payment__body__percentage__icon" />
                                </div>
                                <input
                                  className="staff-registration__payment__body__pay-percentage-input"
                                  placeholder="금액 입력"
                                  type="text"
                                  name="defaultPay"
                                  value={payForm.defaultPay}
                                  onChange={handleNumberValueInput}
                                  maxLength={12}
                                />
                              </div>
                            </div>
                            {payForm.selectedPayType?.label === '시급' && (
                              <div className="staff-registration__payment__body__add-new-template">
                                <div>
                                  {currentMinPay && (
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: '#999',
                                        marginTop: '8px',
                                        marginBottom: '12px',
                                      }}>
                                      {moment(payForm.appStartDate).format('YYYY')}년 최저시급:{' '}
                                      {addComma(currentMinPay)}원
                                    </div>
                                  )}
                                  <div className="staff-registration__payment__body__holiday-allowance">
                                    <CheckCircle
                                      onCheck={(value: boolean) => {
                                        handleChecks(value, 'hourPay_weekPay_use');
                                      }}
                                      value={payForm.hourPay_weekPay_use ?? false}
                                      text="(+) 주휴수당 자동 가산"
                                    />
                                    {payForm.hourPay_weekPay_use && (
                                      <div className="staff-registration__payment__body__holiday-allowance__add-weekly">
                                        <div className="staff-registration__payment__body__holiday-allowance__labour-std-act">
                                          <Radio
                                            isChecked={payForm.hourPay_weekPay_type === '1'}
                                            checked={() => handleChecksType('1', 'hourPay_weekPay_type')}
                                            radioName={'hourPay_weekPay_type'}
                                            text="(자동)근로기준법 기준"
                                          />
                                        </div>
                                        <div className="staff-registration__payment__body__holiday-allowance__labour-std-act">
                                          <Radio
                                            isChecked={payForm.hourPay_weekPay_type === '0'}
                                            checked={() => handleChecksType('0', 'hourPay_weekPay_type')}
                                            radioName={'hourPay_weekPay_type'}
                                            text="(수동)월 근무시간"
                                          />
                                        </div>
                                        {payForm.hourPay_weekPay_type === '0' && (
                                          <div className="staff-registration__payment__body__holiday-allowance__input-container">
                                            <input
                                              type="text"
                                              className="staff-registration__payment__body__holiday-allowance__input"
                                              name="hourPay_weekPay_time"
                                              value={payForm.hourPay_weekPay_time}
                                              onChange={handleTimeValueInput}
                                              maxLength={3}
                                            />
                                            <div className="staff-registration__payment__body__holiday-allowance__input-text">
                                              시간
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="staff-registration__payment__body__holiday-allowance">
                                    <CheckCircle
                                      onCheck={(value: boolean) => {
                                        handleChecks(value, 'hourPay_restTime_use');
                                      }}
                                      value={payForm.hourPay_restTime_use ?? false}
                                      text="(-) 휴게시간 자동 차감"
                                    />
                                    {payForm.hourPay_restTime_use && (
                                      <div className="staff-registration__payment__body__holiday-allowance__add-weekly">
                                        <div className="staff-registration__payment__body__holiday-allowance__labour-std-act">
                                          <Radio
                                            isChecked={payForm.hourPay_restTime_type === '1'}
                                            checked={() => handleChecksType('1', 'hourPay_restTime_type')}
                                            radioName={'hourPay_restTime_type'}
                                            text="(자동)근로기준법 기준"
                                          />
                                        </div>
                                        <div className="staff-registration__payment__body__holiday-allowance__labour-std-act">
                                          <Radio
                                            isChecked={payForm.hourPay_restTime_type === '0'}
                                            checked={() => handleChecksType('0', 'hourPay_restTime_type')}
                                            radioName={'hourPay_restTime_type'}
                                            text="(수동)일 휴게시간"
                                          />
                                        </div>
                                        {payForm.hourPay_restTime_type === '0' && (
                                          <div className="staff-registration__payment__body__holiday-allowance__input-container">
                                            <input
                                              type="text"
                                              className="staff-registration__payment__body__holiday-allowance__input"
                                              name="hourPay_restTime_time"
                                              value={payForm.hourPay_restTime_time}
                                              onChange={handleTimeValueInput}
                                              maxLength={3}
                                            />
                                            <div className="staff-registration__payment__body__holiday-allowance__input-text">
                                              분
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="staff-registration__payment__body__holiday-allowance">
                                    <CheckCircle
                                      onCheck={(value: boolean) => {
                                        handleChecks(value, 'hourPay_allowance_use');
                                      }}
                                      value={payForm.hourPay_allowance_use ?? false}
                                      text="(+) 추가, 야간, 휴일수당 50% 자동 가산"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                            {payForm.selectedPayType?.label === '월급' && (
                              <>
                                <div className="whiteSpace" />
                                <div className="staff-registration__contract__body__pay-percentage-input-set">
                                  <div className="staff-registration__contract__body__pay-percentage-input-text">
                                    식대
                                  </div>
                                  <div className="staff-registration__contract__body__percentage">
                                    <div>
                                      <CircleTextIcon className="staff-registration__payment__body__percentage__icon" />
                                    </div>
                                    <input
                                      className="staff-registration__contract__body__pay-percentage-input"
                                      placeholder="금액 입력"
                                      type="text"
                                      name="monthPay_meal"
                                      value={payForm.monthPay_meal}
                                      onChange={handleNumberValueInput}
                                      maxLength={12}
                                    />
                                  </div>
                                </div>
                                <div className="whiteSpace" />
                                <div className="staff-registration__contract__body__pay-percentage-input-set">
                                  <div className="staff-registration__contract__body__pay-percentage-input-text">
                                    자가운전
                                  </div>
                                  <div className="staff-registration__contract__body__percentage">
                                    <div>
                                      <CircleTextIcon className="staff-registration__payment__body__percentage__icon" />
                                    </div>
                                    <input
                                      className="staff-registration__contract__body__pay-percentage-input"
                                      placeholder="금액 입력"
                                      type="text"
                                      name="monthPay_selfdrive"
                                      value={payForm.monthPay_selfdrive}
                                      onChange={handleNumberValueInput}
                                      maxLength={12}
                                    />
                                  </div>
                                </div>
                                <div className="whiteSpace" />
                                <div className="staff-registration__contract__body__pay-percentage-input-set">
                                  <div className="staff-registration__contract__body__pay-percentage-input-text">
                                    상여
                                  </div>
                                  <div className="staff-registration__contract__body__percentage">
                                    <div>
                                      <CircleTextIcon className="staff-registration__payment__body__percentage__icon" />
                                    </div>
                                    <input
                                      className="staff-registration__contract__body__pay-percentage-input"
                                      placeholder="금액 입력"
                                      type="text"
                                      name="monthPay_bonus"
                                      value={payForm.monthPay_bonus}
                                      onChange={handleNumberValueInput}
                                      maxLength={12}
                                    />
                                  </div>
                                </div>
                                <div className="whiteSpace" />
                                <div className="staff-registration__contract__body__pay-percentage-input-set">
                                  <div className="staff-registration__contract__body__pay-percentage-input-text">
                                    성과급
                                  </div>
                                  <div className="staff-registration__contract__body__percentage">
                                    <div>
                                      <CircleTextIcon className="staff-registration__payment__body__percentage__icon" />
                                    </div>
                                    <input
                                      className="staff-registration__contract__body__pay-percentage-input"
                                      placeholder="금액 입력"
                                      type="text"
                                      name="monthPay_incentive"
                                      value={payForm.monthPay_incentive}
                                      onChange={handleNumberValueInput}
                                      maxLength={12}
                                    />
                                  </div>
                                </div>
                              </>
                            )}
                            <div className="staff-registration__payment__body__add-new-template">
                              <div className="staff-registration__payment__body__label">소득신고</div>
                              <div>
                                <Select
                                  className="staff-registration__payment__body__input3"
                                  components={{
                                    IndicatorSeparator: () => null,
                                    DropdownIndicator: () => (
                                      <div style={{ paddingRight: '15px' }}>
                                        <ArrowDownIcon />
                                      </div>
                                    ),
                                  }}
                                  placeholder="4대보험"
                                  styles={colourStyles}
                                  value={payForm.selectedDeducType}
                                  options={deducTypeOptions}
                                  onChange={(newValue) => handleTypeChange(newValue, 'deduc')}
                                />
                              </div>
                              {payForm.selectedDeducType?.label === '4대보험' && (
                                <div className="staff-registration__payment__body__income-report">
                                  <div className="staff-registration__payment__body__income-report__checkbox">
                                    <CheckBox
                                      checked={handleCheckBoxInput}
                                      defaultCheck={payForm.deduc_penstion ?? false}
                                      text="국민연금"
                                      checkBoxname="deduc_penstion"
                                    />
                                  </div>
                                  <div className="staff-registration__payment__body__income-report__checkbox">
                                    <CheckBox
                                      checked={handleCheckBoxInput}
                                      defaultCheck={payForm.deduc_health ?? false}
                                      text="건강보험"
                                      checkBoxname="deduc_health"
                                    />
                                  </div>
                                  <div className="staff-registration__payment__body__income-report__checkbox">
                                    <CheckBox
                                      checked={handleCheckBoxInput}
                                      defaultCheck={payForm.deduc_employeement ?? false}
                                      text="고용보험"
                                      checkBoxname="deduc_employeement"
                                    />
                                  </div>
                                  <div className="staff-registration__payment__body__income-report__checkbox">
                                    <CheckBox
                                      checked={handleCheckBoxInput}
                                      defaultCheck={payForm.deduc_accident ?? false}
                                      text="산재보험"
                                      checkBoxname="deduc_accident"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {existingTemplate && (
                          <div className="staff-registration__payment__body__existing-template">
                            <table>
                              <tr>
                                <td className="staff-registration__payment__body__existing-template__td">급여정보</td>
                                <td className="staff-registration__payment__body__existing-template__text">
                                  <div className="staff-registration__payment__body__existing-template__label">
                                    {currentExistingTemplate.rate === 'HOURLY' && (
                                      <Label text="시급" type="custom" color_type="#ECE1F6" />
                                    )}
                                    {currentExistingTemplate.rate === 'WEEKLY' && (
                                      <Label text="주급" type="custom" color_type="#FDD8C3" />
                                    )}
                                    {currentExistingTemplate.rate === 'MONTHLY' && (
                                      <Label text="월급" type="custom" color_type="#FDE9C3" />
                                    )}
                                  </div>
                                  {currentExistingTemplate.salaryInfo}원
                                </td>
                              </tr>
                              <tr>
                                <td className="staff-registration__payment__body__existing-template__td">주휴수당</td>
                                <td className="staff-registration__payment__body__existing-template__text">
                                  {currentExistingTemplate.allowance}
                                </td>
                              </tr>
                              <tr>
                                <td className="staff-registration__payment__body__existing-template__td">소득신고</td>
                                <td className="staff-registration__payment__body__existing-template__text">
                                  {currentExistingTemplate.incomeReport}
                                </td>
                              </tr>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="staff-registration__card__right">
                  <div>
                    <div className="staff-registration__right-heads">적용 시작월</div>
                    <div className="staff-registration__right-body">시작월 부터 자동으로 급여가 계산됩니다.</div>
                  </div>
                  <div className="staff-registration__right-top">
                    <div className="staff-registration__right-heads">근로 템플릿이란?</div>
                    <div className="staff-registration__right-body">
                      직원들의 급여 정보를 템플릿으로 저장해두고 한번에 적용시킬 수 있어요
                    </div>
                  </div>
                </div>
              </div>
            )}
            {formType === '수습계약' && (
              <div className="staff-registration__card">
                <div
                  className={
                    probationForm.use
                      ? 'staff-registration__contract__left-enabled'
                      : 'staff-registration__contract__left'
                  }>
                  <div className="staff-registration__payment">
                    <div className="staff-registration__contract__up">
                      <div className="staff-registration__contract__head">수습계약</div>
                      <div className="staff-registration__contract__toggle">
                        <Toggle toggled={probationForm.use} onClick={toggleContract} />
                      </div>
                    </div>
                    {probationForm.use && (
                      <div className="staff-registration__contract__body">
                        <div className="staff-registration__contract__body__pay-percentage">
                          <div className="staff-registration__contract__body__end">
                            <DateSelector
                              onChange={(date) => handleDateChange(date, 'probationForm_end')}
                              value={probationForm.endDate}
                              label="수습계약 종료일*"
                              height={56}
                              placeholder="종료일 선택"
                              minDate={payForm.startDate}
                            />
                          </div>
                        </div>
                        <div className="staff-registration__contract__body__pay-percentage">
                          <div className="staff-registration__contract__body__pay-percentage-head">수습 급여 비율*</div>
                          <div className="staff-registration__contract__body__pay-percentage-input-set">
                            <div className="staff-registration__contract__body__pay-percentage-input-text">
                              급여지급율
                            </div>
                            <div className="staff-registration__contract__body__percentage">
                              <div className="staff-registration__contract__body__percentage">
                                <PercentageIcon className="staff-registration__contract__body__percentage__icon" />
                              </div>
                              <input
                                className="staff-registration__contract__body__pay-percentage-input"
                                placeholder="숫자 입력"
                                type="text"
                                name="rate"
                                value={probationForm.rate}
                                onChange={handlePercentValueInput}
                                max={100}
                                maxLength={3}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {probationForm.use && (
                  <div className="staff-registration__card__right">
                    <div>
                      <div className="staff-registration__right-heads">급여계산</div>
                      <div className="staff-registration__right-body">
                        입력하신 ‘수습계약 시작일'부터 ‘수습계약 종료일’ 까지의 기간 동안 ‘급여지급율’에 따라 일할
                        계산됩니다
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </PlainNavbar>
    </>
  );
};

export default WorkSchedule;
